import { REGION_BOUNDS } from "../dataLists/regionBounds";
import { dataListsType } from "../shared/type";

export type LatLng = { lat: number; lng: number };

export const NEARBY_RADIUS_OPTIONS = [3, 5, 10] as const;
export type NearbyRadiusKm = (typeof NEARBY_RADIUS_OPTIONS)[number];

export const prefectureToSlug = (prefecture: string): string =>
  prefecture.toLowerCase().trim().replace(/\s+/g, "-");

export const slugToPrefecture = (slug: string): string =>
  slug.replace(/-/g, " ");

export function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistanceKm(distanceKm: number): string {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  if (distanceKm < 10) {
    return `${distanceKm.toFixed(1)} km`;
  }
  return `${Math.round(distanceKm)} km`;
}

export function getStorePosition(store: dataListsType): LatLng | null {
  if (store.lat == null || store.lng == null) return null;
  return { lat: store.lat, lng: store.lng };
}

export function buildStoreDistanceMap(
  stores: dataListsType[],
  userPosition: LatLng,
): Map<number, number> {
  const distances = new Map<number, number>();

  for (const store of stores) {
    const pos = getStorePosition(store);
    if (!pos) continue;
    distances.set(
      store.id,
      haversineKm(userPosition.lat, userPosition.lng, pos.lat, pos.lng),
    );
  }

  return distances;
}

export function filterStoresWithinRadius(
  stores: dataListsType[],
  userPosition: LatLng,
  radiusKm: number,
): dataListsType[] {
  return stores.filter((store) => {
    const pos = getStorePosition(store);
    if (!pos) return false;
    return haversineKm(userPosition.lat, userPosition.lng, pos.lat, pos.lng) <= radiusKm;
  });
}

export function findNearbyStoresWithAutoExpand(
  stores: dataListsType[],
  userPosition: LatLng,
  preferredRadiusKm: NearbyRadiusKm,
): {
  stores: dataListsType[];
  effectiveRadiusKm: number;
  exhausted: boolean;
} {
  const radiiToTry =
    preferredRadiusKm === 3
      ? [3, 5, 10]
      : preferredRadiusKm === 5
        ? [5, 10]
        : [10];

  for (const radiusKm of radiiToTry) {
    const matched = filterStoresWithinRadius(stores, userPosition, radiusKm);
    if (matched.length > 0) {
      return {
        stores: matched,
        effectiveRadiusKm: radiusKm,
        exhausted: false,
      };
    }
  }

  return {
    stores: [],
    effectiveRadiusKm: 10,
    exhausted: true,
  };
}

export function detectPrefectureFromCoords(
  position: LatLng,
): string | null {
  let bestPrefecture: string | null = null;
  let bestDistance = Infinity;

  for (const region of REGION_BOUNDS) {
    const distanceToMetro = haversineKm(
      position.lat,
      position.lng,
      region.metroCenter.lat,
      region.metroCenter.lng,
    );

    if (distanceToMetro <= region.metroRadiusKm && distanceToMetro < bestDistance) {
      bestPrefecture = region.prefecture;
      bestDistance = distanceToMetro;
    }
  }

  return bestPrefecture;
}

export function prefecturesMatch(
  a: string | undefined,
  b: string | undefined,
): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}
